// src/services/offlineQueue.js
//
// Offline mutation queue: when the browser is offline and a write request
// fails, we stash it here. When the browser comes back online we replay
// each entry in order, one at a time, against the live axios instance.
//
// Scope intentionally narrow for the first pass:
//   - Only POST / PUT / PATCH / DELETE are queued. GETs rely on the
//     service-worker cache and simply fail at the call site.
//   - The queue is persisted to localStorage under `caro.portal.offline_queue`.
//     IndexedDB would be more generous on storage caps, but a few dozen
//     pending mutations fits localStorage comfortably and keeps setup simple.
//   - No conflict resolution — replays are optimistic; a 4xx/5xx during
//     replay marks the entry as failed and surfaces it to the user.
//
// The store exposes a small subscribe/snapshot API so React components can
// reflect it via `useSyncExternalStore`.

const STORAGE_KEY = 'caro.portal.offline_queue';

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const save = (entries) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* storage full / disabled — accept the loss */
  }
};

const listeners = new Set();
let entries = load();
let replayInFlight = false;
let replayClient = null;

const notify = () => {
  listeners.forEach((fn) => fn());
};

const genId = () =>
  `oq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const offlineQueue = {
  /**
   * Subscribe to queue changes. Compatible with `useSyncExternalStore`.
   */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** Returns the current entries array (stable until next mutation). */
  getSnapshot() {
    return entries;
  },

  /** Count of pending (non-failed) entries. Useful for banners. */
  pendingCount() {
    return entries.filter((e) => e.status !== 'failed').length;
  },

  /**
   * Register the axios instance used to replay. Called once at app boot
   * so the queue module doesn't create a circular dependency by importing
   * from apiClient directly.
   */
  bindClient(client) {
    replayClient = client;
  },

  /** Enqueue a mutation. Returns the assigned entry id. */
  enqueue({ method, url, data, config, description }) {
    const entry = {
      id: genId(),
      method: String(method || 'post').toLowerCase(),
      url,
      data,
      // Only persist safe-to-replay config keys.
      config: config ? { headers: config.headers, params: config.params } : undefined,
      description: description || `${String(method).toUpperCase()} ${url}`,
      enqueuedAt: Date.now(),
      retryCount: 0,
      status: 'pending',
      error: null,
    };
    entries = [...entries, entry];
    save(entries);
    notify();
    return entry.id;
  },

  remove(id) {
    entries = entries.filter((e) => e.id !== id);
    save(entries);
    notify();
  },

  clearFailed() {
    entries = entries.filter((e) => e.status !== 'failed');
    save(entries);
    notify();
  },

  /**
   * Replay pending entries in FIFO order. No-op if already replaying or
   * if the browser is still offline. Stops on the first failure and
   * marks that entry as failed so the user can retry.
   */
  async replay() {
    if (replayInFlight) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (!replayClient) return;
    replayInFlight = true;
    try {
      // Work against a snapshot; additions during replay are handled on
      // the next tick.
      const pending = entries.filter((e) => e.status === 'pending');
      for (const entry of pending) {
        try {
          await replayClient.request({
            method: entry.method,
            url: entry.url,
            data: entry.data,
            headers: entry.config?.headers,
            params: entry.config?.params,
            // Prevent our own interceptor from re-queueing if this fails
            // due to a non-network error.
            __offlineReplay: true,
          });
          entries = entries.filter((e) => e.id !== entry.id);
        } catch (err) {
          // Network error → keep it pending for the next reconnect.
          // Everything else (4xx/5xx) → permanent failure.
          const isNetworkErr = !err?.response;
          if (isNetworkErr) {
            // Stop draining; try again later.
            save(entries);
            notify();
            return;
          }
          entries = entries.map((e) =>
            e.id === entry.id
              ? { ...e, status: 'failed', error: err?.response?.data?.error || err?.message || 'Request failed', retryCount: e.retryCount + 1 }
              : e,
          );
        }
        save(entries);
        notify();
      }
    } finally {
      replayInFlight = false;
    }
  },
};

// Auto-replay on reconnect, and once more on app boot in case we have
// stale entries from a previous offline session.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    offlineQueue.replay();
  });
  // Defer boot replay so the apiClient has a chance to bindClient() first.
  setTimeout(() => {
    if (navigator.onLine && entries.some((e) => e.status === 'pending')) {
      offlineQueue.replay();
    }
  }, 2000);
}

export default offlineQueue;
